// Phase 5.0 — ambient /system/status poll that drives the
// workspace-rail health badge and the /status diagnostic page.
//
// Polling cadence: 60s, hardcoded (the brief explicitly says no
// settings UI for it). Global rather than per-route — the brief
// gave us the choice and "treat as global ambient state" was
// the simpler shape.
//
// Lifecycle:
//   - Provider mounts at the App.tsx level, INSIDE the
//     authenticated subtree. Polling never fires unless the
//     user is logged in (otherwise every tick would 401 and
//     trigger an instant logout via the lib/api.ts handler).
//   - On mount, kick off an immediate fetch so badges aren't
//     blank for the first 60s.
//   - setInterval at 60_000ms. Each tick swaps in the fresh
//     payload; failure logs + leaves the prior value (the
//     `as_of` timestamp will start to look stale on the FE,
//     which is the operator-visible signal that something's
//     off).
//   - useEffect cleanup clears the interval on unmount.
//
// `refresh()` exported so the /status page's "Refresh now"
// button can force an immediate fetch without waiting out the
// 60s tick. Returns a promise so the button can show a brief
// loading state.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { ApiError, getSystemStatus, type SystemStatusResponse } from "../lib/api";

const POLL_INTERVAL_MS = 60_000;

export interface SystemStatusState {
  /** Most recent successful payload, or null before the first
   *  fetch completes. */
  status: SystemStatusResponse | null;
  /** True while the very first fetch is in flight. Stays false
   *  on subsequent polls — the UI doesn't re-skeleton every
   *  minute. */
  loading: boolean;
  /** Most recent fetch error, or null. Persists across successful
   *  polls only until the next success resets it. */
  error: ApiError | null;
  /** Force an immediate fetch. Used by /status's "Refresh now"
   *  button. */
  refresh: () => Promise<void>;
}

const Ctx = createContext<SystemStatusState | undefined>(undefined);

export interface SystemStatusProviderProps {
  children: ReactNode;
  /** Override the poll interval for tests so they don't have to
   *  wait 60s. Production code never passes this. */
  pollIntervalMs?: number;
}

export function SystemStatusProvider({
  children,
  pollIntervalMs = POLL_INTERVAL_MS,
}: SystemStatusProviderProps) {
  const [status, setStatus] = useState<SystemStatusResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<ApiError | null>(null);

  // Track whether we've ever completed a fetch so subsequent
  // polls don't flip `loading` back to true (which would re-
  // skeleton the UI every minute).
  const everCompletedRef = useRef(false);
  // AbortController for the in-flight fetch so a fast unmount
  // doesn't leave a pending fetch trying to call setState.
  const inFlightRef = useRef<AbortController | null>(null);

  const doFetch = useCallback(async () => {
    inFlightRef.current?.abort();
    const ac = new AbortController();
    inFlightRef.current = ac;
    try {
      const next = await getSystemStatus(ac.signal);
      if (ac.signal.aborted) return;
      setStatus(next);
      setError(null);
    } catch (e) {
      if (ac.signal.aborted) return;
      // Network / 4xx / 5xx — log to the console for the operator
      // and surface via error state. Leave `status` alone so the
      // UI keeps showing the last good payload.
      if (e instanceof ApiError) {
        setError(e);
      } else {
        setError(new ApiError(String(e), 0));
      }
    } finally {
      if (!ac.signal.aborted) {
        if (!everCompletedRef.current) {
          everCompletedRef.current = true;
          setLoading(false);
        }
      }
    }
  }, []);

  useEffect(() => {
    // Kick off the first fetch immediately; don't wait 60s.
    void doFetch();
    const handle = window.setInterval(() => {
      void doFetch();
    }, pollIntervalMs);
    return () => {
      window.clearInterval(handle);
      inFlightRef.current?.abort();
    };
  }, [doFetch, pollIntervalMs]);

  const refresh = useCallback(async () => {
    await doFetch();
  }, [doFetch]);

  const value = useMemo<SystemStatusState>(
    () => ({ status, loading, error, refresh }),
    [status, loading, error, refresh],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** Consume the ambient system-status state. Throws if used outside
 *  a SystemStatusProvider — that's a hard misuse (a component
 *  outside the authenticated subtree is asking for a payload the
 *  unauthenticated user has no business seeing). */
export function useSystemStatus(): SystemStatusState {
  const ctx = useContext(Ctx);
  if (ctx === undefined) {
    throw new Error("useSystemStatus must be used inside a SystemStatusProvider");
  }
  return ctx;
}
